/**
 * MOB drift & search — behind the second press of the MOB button. Report
 * fb-ms1ipa0m-3, extended with the RNLI Rapid Response search area.
 *
 * Three jobs, all off the RNLI ALB SAR Check Cards:
 *   1. Drift the casualty. Take the last-known position and time, the casualty
 *      type (which sets how much leeway they make), the wind, and the tide over
 *      the time they've been in, and estimate where they've drifted to. Card 10.
 *   2. Size the search area. From the resultant drift and the fix error, work a
 *      search radius, draw the circle around the datum, and square it off into a
 *      search box. RNLI Rapid Response Method (old ALB card TP-ALB-133).
 *   3. Work a search. Either a through-water pattern (expanding square / sector
 *      search) as a leg-by-leg instruction list — a compass heading, a speed
 *      through the water, and a time to run before the turn, with a live
 *      countdown — or a ground-based area pattern (parallel track / creeping
 *      line) plotted on the chart with a compact leg summary.
 *
 * Edd's rule holds for the through-water patterns: we do NOT plot them, because a
 * helm can't read a spiral in a real emergency. They steer a heading and turn on
 * a timer. The area patterns are run over the ground, so they CAN and SHOULD be
 * plotted, and they are.
 *
 * The estimate is a datum to start from, not a fix. The RNLI method holds for
 * about the first hour of drift; past that the tide has usually turned and the
 * single averaged vector stops describing where the casualty went, so we flag it.
 */
import { useEffect, useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useSafetyStore } from '@/store/safety';
import { useEnvStore } from '@/store/environment';
import { useMobSearchStore } from '@/store/mobSearch';
import { useBearingDisplayStore } from '@/store/bearingDisplay';
import {
  CASUALTY_TYPES,
  casualtyTypeById,
  casualtyDriftEstimate,
  averageComponents,
  mobRangeBearing,
  buildPattern,
  patternProgress,
  formatLegTime,
  searchRadiusNm,
  searchBoxFromRadius,
  areaSearchLegs,
  isAreaPattern,
  IPE_OPTIONS,
  ipeOptionById,
  DRIFT_RELIABLE_MIN,
  type DriftComponent,
  type MobMark,
} from '@/safety';
import { streamAtPosition } from '@/tides';
import { formatBearing, formatDistance, formatLatLon } from '@/nav';
import { Sheet } from './Sheet';
import { GlossaryTerm } from '@/help/GlossaryTerm';

/** A plain 000°–359° heading with its cardinal, e.g. "090° E". Not °T/°M: it's a
 *  compass heading to steer, as the RNLI cards give it. */
function headingLabel(deg: number, cardinal: string): string {
  return `${String(Math.round(deg) % 360).padStart(3, '0')}° ${cardinal}`;
}

function elapsedLabel(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function posLabel(p: [number, number]): string {
  return `${formatLatLon(p[1], 'lat')}  ${formatLatLon(p[0], 'lon')}`;
}

/**
 * The average tidal stream at the casualty over the time they've been adrift,
 * from the app's tidal-stream data. We sample the stream at the drop position at
 * a handful of instants across the interval (ending at the app's shown stream
 * time) and vector-average them, so a tide that eased or turned over the interval
 * reduces to the mean push it actually gave.
 */
function averagedTideAtMob(
  pos: [number, number],
  endMs: number,
  elapsedMin: number,
): DriftComponent {
  const spanMs = Math.max(0, elapsedMin) * 60_000;
  const N = 6;
  const samples: DriftComponent[] = [];
  for (let i = 0; i <= N; i++) {
    const t = endMs - spanMs + (spanMs * i) / N;
    const s = streamAtPosition(pos, t);
    samples.push({ setDeg: s.setDeg, rateKn: s.rateKn });
  }
  return averageComponents(samples);
}

/** A number input that lets you type freely and commits a finite value. */
function NumberField(props: {
  label: string;
  value: number;
  onCommit: (n: number) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  const [txt, setTxt] = useState(String(props.value));
  useEffect(() => {
    // Resync when the store value changes elsewhere (e.g. a fresh-MOB reset),
    // but not while the field is mid-edit and already numerically in step.
    if (txt.trim() !== '' && parseFloat(txt) !== props.value) setTxt(String(props.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);
  return (
    <>
      <label className="acp-fieldlabel">{props.label}</label>
      <input
        className="acp-text-input"
        value={txt}
        inputMode="decimal"
        placeholder={props.placeholder}
        aria-label={props.ariaLabel}
        onChange={(e) => {
          const v = e.target.value;
          setTxt(v);
          const n = parseFloat(v);
          if (Number.isFinite(n)) props.onCommit(n);
        }}
      />
    </>
  );
}

/**
 * Split a signed decimal degree into degrees / decimal-minutes / hemisphere for
 * one axis, so the last-known position can be edited the way the rest of the app
 * shows positions (DMm, never decimal degrees). Minutes are rounded to 3 dp
 * (about 1.8 m), which is all the precision the fix is ever good to.
 */
function dmmParts(value: number, axis: 'lat' | 'lon'): { deg: number; min: number; hemi: string } {
  const abs = Math.abs(value);
  const deg = Math.trunc(abs);
  const min = Math.round((abs - deg) * 60 * 1000) / 1000;
  const hemi = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
  return { deg, min, hemi };
}

/** Rebuild a signed decimal degree from DMm parts (the inverse of dmmParts). */
function fromDmm(deg: number, min: number, hemi: string): number {
  const sign = hemi === 'S' || hemi === 'W' ? -1 : 1;
  return sign * (Math.abs(deg) + Math.abs(min) / 60);
}

export function MobSearchSheet() {
  const mob = useSafetyStore((s) => s.mob);
  const setOverlay = useMobSearchStore((s) => s.setOverlay);

  // No active MOB means no search area to draw: clear whatever was on the chart.
  useEffect(() => {
    if (!mob) setOverlay(null);
  }, [mob, setOverlay]);

  if (!mob) {
    return (
      <Sheet name="mobsearch" title="Man overboard — drift &amp; search">
        <p className="acp-note">No man overboard is active. Drop a MOB first, then this is where the drift estimate and search patterns live.</p>
      </Sheet>
    );
  }

  return <MobSearchActive mob={mob} />;
}

/**
 * The live sheet, mounted only while a MOB is running so the drift, radius, box
 * and search geometry can all recompute every second without hook-order games.
 */
function MobSearchActive({ mob }: { mob: MobMark }) {
  const boat = useChartPlotterStore((s) => s.ownship.position);
  const envWindDir = useEnvStore((s) => s.windDirDeg);
  const envWindKn = useEnvStore((s) => s.windSpeedKn);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  useBearingDisplayStore((s) => s.mode);
  useBearingDisplayStore((s) => s.variationDeg);

  const casualtyId = useMobSearchStore((s) => s.casualtyId);
  const setCasualtyId = useMobSearchStore((s) => s.setCasualtyId);
  const useAppWind = useMobSearchStore((s) => s.useAppWind);
  const setUseAppWind = useMobSearchStore((s) => s.setUseAppWind);
  const windFromDeg = useMobSearchStore((s) => s.windFromDeg);
  const windKn = useMobSearchStore((s) => s.windKn);
  const setWind = useMobSearchStore((s) => s.setWind);
  const ipeId = useMobSearchStore((s) => s.ipeId);
  const ipeNm = useMobSearchStore((s) => s.ipeNm);
  const setIpe = useMobSearchStore((s) => s.setIpe);
  const trackSpacingNm = useMobSearchStore((s) => s.trackSpacingNm);
  const setTrackSpacingNm = useMobSearchStore((s) => s.setTrackSpacingNm);
  const patternSpeedKn = useMobSearchStore((s) => s.patternSpeedKn);
  const setPatternSpeedKn = useMobSearchStore((s) => s.setPatternSpeedKn);
  const patternId = useMobSearchStore((s) => s.patternId);
  const setPatternId = useMobSearchStore((s) => s.setPatternId);
  const patternStartedAt = useMobSearchStore((s) => s.patternStartedAt);
  const startPattern = useMobSearchStore((s) => s.startPattern);
  const stopPattern = useMobSearchStore((s) => s.stopPattern);
  const setOverlay = useMobSearchStore((s) => s.setOverlay);
  const setMob = useSafetyStore((s) => s.setMob);

  // One-second tick so the elapsed time, drift datum, search area and leg
  // countdown all keep live while the sheet is open and a MOB is running.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now = Date.now();
  const elapsedMs = now - mob.droppedAt;
  const elapsedMin = elapsedMs / 60_000;
  const casualty = casualtyTypeById(casualtyId);
  const effWindDir = useAppWind ? envWindDir : windFromDeg;
  const effWindKn = useAppWind ? envWindKn : windKn;
  const tide = averagedTideAtMob(mob.position, streamTimeMs, elapsedMin);
  const drift = casualtyDriftEstimate({
    dsp: mob.position,
    elapsedMin,
    tide,
    casualty,
    windFromDeg: effWindDir,
    windKn: effWindKn,
  });
  const toDatum = boat ? mobRangeBearing(boat, drift.datum) : null;

  // Rapid Response search area: radius from the resultant drift + the fix error,
  // then a datum-centred box that encloses the circle.
  const radius = searchRadiusNm(drift.driftNm, ipeNm);
  const box = searchBoxFromRadius(drift.datum, radius.radiusNm);

  // The chosen pattern: a ground-based area search (plotted) or a through-water
  // pattern (heading + timer, not plotted).
  const area = isAreaPattern(patternId)
    ? areaSearchLegs(patternId, box, trackSpacingNm, patternSpeedKn)
    : null;
  const waterPattern = !isAreaPattern(patternId)
    ? buildPattern(patternId, trackSpacingNm, patternSpeedKn)
    : null;
  const progress =
    waterPattern && patternStartedAt ? patternProgress(waterPattern.legs, patternStartedAt, now) : null;

  // Publish the search geometry for the chart. Datum, radius, box and (for an
  // area pattern) the plotted track are all pure functions of these values, so
  // depending on the primitives keeps the overlay in step without object churn.
  useEffect(() => {
    setOverlay({
      datum: drift.datum,
      radiusNm: radius.radiusNm,
      boxRing: box.ring,
      boxCorners: { A: box.A, B: box.B, C: box.C, D: box.D },
      area: area ? { id: area.id, vertices: area.vertices, csp: area.csp } : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drift.datum[0], drift.datum[1], radius.radiusNm, patternId, trackSpacingNm, patternSpeedKn]);

  return (
    <Sheet
      name="mobsearch"
      title="Man overboard — drift &amp; search"
      sub="Estimate where the casualty has drifted, size the search area, then work a pattern."
    >
      {/* ---- Last-known position (editable) ---- */}
      <h3 className="acp-subhead">Last-known position</h3>
      <p className="acp-note" style={{ marginTop: 0 }}>
        Where the casualty went in. Drag the MOB mark on the chart, or type the fix
        here; the drift datum and search area below recompute from it.
      </p>
      <div className="acp-two-col">
        <NumberField
          label="Latitude (°)"
          value={dmmParts(mob.position[1], 'lat').deg}
          onCommit={(n) => {
            const p = dmmParts(mob.position[1], 'lat');
            setMob({ ...mob, position: [mob.position[0], fromDmm(n, p.min, p.hemi)] });
          }}
          ariaLabel="Casualty last-known latitude, whole degrees"
        />
        <NumberField
          label="Latitude (min)"
          value={dmmParts(mob.position[1], 'lat').min}
          onCommit={(n) => {
            const p = dmmParts(mob.position[1], 'lat');
            setMob({ ...mob, position: [mob.position[0], fromDmm(p.deg, n, p.hemi)] });
          }}
          ariaLabel="Casualty last-known latitude, decimal minutes"
        />
      </div>
      <label className="acp-fieldlabel">North / South</label>
      <select
        className="acp-select"
        value={dmmParts(mob.position[1], 'lat').hemi}
        onChange={(e) => {
          const p = dmmParts(mob.position[1], 'lat');
          setMob({ ...mob, position: [mob.position[0], fromDmm(p.deg, p.min, e.target.value)] });
        }}
        aria-label="Casualty last-known latitude hemisphere"
      >
        <option value="N">N</option>
        <option value="S">S</option>
      </select>
      <div className="acp-two-col" style={{ marginTop: 10 }}>
        <NumberField
          label="Longitude (°)"
          value={dmmParts(mob.position[0], 'lon').deg}
          onCommit={(n) => {
            const p = dmmParts(mob.position[0], 'lon');
            setMob({ ...mob, position: [fromDmm(n, p.min, p.hemi), mob.position[1]] });
          }}
          ariaLabel="Casualty last-known longitude, whole degrees"
        />
        <NumberField
          label="Longitude (min)"
          value={dmmParts(mob.position[0], 'lon').min}
          onCommit={(n) => {
            const p = dmmParts(mob.position[0], 'lon');
            setMob({ ...mob, position: [fromDmm(p.deg, n, p.hemi), mob.position[1]] });
          }}
          ariaLabel="Casualty last-known longitude, decimal minutes"
        />
      </div>
      <label className="acp-fieldlabel">East / West</label>
      <select
        className="acp-select"
        value={dmmParts(mob.position[0], 'lon').hemi}
        onChange={(e) => {
          const p = dmmParts(mob.position[0], 'lon');
          setMob({ ...mob, position: [fromDmm(p.deg, p.min, e.target.value), mob.position[1]] });
        }}
        aria-label="Casualty last-known longitude hemisphere"
      >
        <option value="E">E</option>
        <option value="W">W</option>
      </select>
      <div className="acp-kv" style={{ marginTop: 10 }}>
        <span className="k">Last-known position</span>
        <span className="v">{posLabel(mob.position)}</span>
      </div>

      {/* ---- Drift / datum (RNLI Card 10) ---- */}
      <h3 className="acp-subhead" style={{ marginTop: 16 }}>Drift estimate</h3>

      <label className="acp-fieldlabel">Casualty type</label>
      <select
        className="acp-select"
        value={casualtyId}
        onChange={(e) => setCasualtyId(e.target.value)}
        aria-label="Casualty type for the leeway estimate"
      >
        {CASUALTY_TYPES.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>

      <div className="acp-layer" style={{ marginTop: 10 }}>
        <div className="lt">
          <b>Use the app&rsquo;s wind</b>
          <span>
            {useAppWind
              ? `Drifting with ${formatBearing(envWindDir)} at ${envWindKn.toFixed(0)} kn.`
              : 'Enter the wind by hand below.'}
          </span>
        </div>
        <button
          type="button"
          className={`acp-sw${useAppWind ? ' on' : ''}`}
          aria-pressed={useAppWind}
          aria-label="Use the app wind for leeway"
          onClick={() => setUseAppWind(!useAppWind)}
        />
      </div>
      {!useAppWind && (
        <div className="acp-two-col">
          <NumberField
            label="Wind from (°T)"
            value={windFromDeg}
            onCommit={(n) => setWind(((n % 360) + 360) % 360, windKn)}
            ariaLabel="Wind direction it blows from, degrees true"
            placeholder="270"
          />
          <NumberField
            label="Wind speed (kn)"
            value={windKn}
            onCommit={(n) => setWind(windFromDeg, Math.max(0, n))}
            ariaLabel="Wind speed in knots"
            placeholder="10"
          />
        </div>
      )}

      <div className="acp-kv" style={{ marginTop: 10 }}>
        <span className="k">Adrift for</span>
        <span className="v">{elapsedLabel(elapsedMs)}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Tide (avg over interval)</span>
        <span className="v">{formatBearing(tide.setDeg)} at {tide.rateKn.toFixed(1)} kn · {drift.tideNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k"><GlossaryTerm termKey="leeway">Leeway</GlossaryTerm> (downwind)</span>
        <span className="v">{formatBearing(drift.leeway.setDeg)} at {drift.leeway.rateKn.toFixed(2)} kn · {drift.leewayNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k">Drifted from mark</span>
        <span className="v">{formatDistance(drift.driftNm)} · {formatBearing(drift.driftSetDeg)}</span>
      </div>
      <div className="acp-kv">
        <span className="k"><GlossaryTerm termKey="ep">Estimated position</GlossaryTerm></span>
        <span className="v">{posLabel(drift.datum)}</span>
      </div>
      <div className="acp-kv">
        <span className="k">From the boat to datum</span>
        <span className="v">{toDatum ? `${formatBearing(toDatum.bearingDeg)} · ${formatDistance(toDatum.rangeNm)}` : 'No fix'}</span>
      </div>

      <p className={`acp-note${drift.beyondReliable ? ' acp-warn' : ''}`}>
        {drift.beyondReliable
          ? `Adrift over ${DRIFT_RELIABLE_MIN} minutes — the RNLI method is built for about the first hour. Past that the tide has usually turned, so treat this datum with real caution and widen the search.`
          : 'RNLI Card 10 estimate. A datum to start from, not a fix. Reliable for roughly the first hour of drift.'}
      </p>

      {/* ---- Search area (RNLI Rapid Response Method) ---- */}
      <h3 className="acp-subhead" style={{ marginTop: 16 }}>Search area</h3>

      <label className="acp-fieldlabel">Navigational fix error (IPE)</label>
      <select
        className="acp-select"
        value={ipeId}
        onChange={(e) => {
          const id = e.target.value;
          if (id === 'manual') setIpe('manual', ipeNm);
          else setIpe(id, ipeOptionById(id)?.nm ?? ipeNm);
        }}
        aria-label="Initial position error for the search radius"
      >
        {IPE_OPTIONS.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label} — {o.nm} nm{o.note ? ` (${o.note})` : ''}
          </option>
        ))}
        <option value="manual">Manual entry…</option>
      </select>
      {ipeId === 'manual' && (
        <div style={{ marginTop: 8 }}>
          <NumberField
            label="Fix error (nm)"
            value={ipeNm}
            onCommit={(n) => setIpe('manual', Math.max(0, n))}
            ariaLabel="Manual navigational fix error in nautical miles"
            placeholder="0.3"
          />
        </div>
      )}

      <div className="acp-kv" style={{ marginTop: 10 }}>
        <span className="k">Search radius</span>
        <span className="v">{radius.radiusNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k">Drift error (drift ÷ 3)</span>
        <span className="v">{drift.driftNm.toFixed(2)} ÷ 3 = {radius.driftErrorNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k">Fix error (IPE)</span>
        <span className="v">{radius.ipeNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k">Search box (side 2 × radius)</span>
        <span className="v">{box.lengthNm.toFixed(2)} nm square · corners A–D</span>
      </div>

      <p className="acp-note">
        Search radius = drift error (a third of the resultant drift) + the fix error. The circle is drawn on the chart around the datum, with the square search box that just encloses it, corners A (NW), B (NE), C (SE), D (SW).
      </p>
      <p className="acp-note acp-warn">
        Rapid Response Method — valid for roughly the first hour from when the drift started. Past that a full search area including divergence is needed, which the RNLI cards here don&rsquo;t cover, so we don&rsquo;t guess at it.
      </p>

      {/* ---- Search pattern ---- */}
      <h3 className="acp-subhead" style={{ marginTop: 16 }}>Search pattern</h3>

      <div className="acp-seg">
        <button
          type="button"
          className={patternId === 'expanding_square' ? 'on' : ''}
          onClick={() => setPatternId('expanding_square')}
        >
          Expanding square
        </button>
        <button
          type="button"
          className={patternId === 'sector_search' ? 'on' : ''}
          onClick={() => setPatternId('sector_search')}
        >
          Sector search
        </button>
        <button
          type="button"
          className={patternId === 'parallel_track' ? 'on' : ''}
          onClick={() => setPatternId('parallel_track')}
        >
          Parallel track
        </button>
        <button
          type="button"
          className={patternId === 'creeping_line' ? 'on' : ''}
          onClick={() => setPatternId('creeping_line')}
        >
          Creeping line
        </button>
      </div>

      <div className="acp-two-col" style={{ marginTop: 8 }}>
        <NumberField
          label={patternId === 'expanding_square' ? 'Initial track spacing (nm)' : 'Track spacing (nm)'}
          value={trackSpacingNm}
          onCommit={(n) => setTrackSpacingNm(Math.max(0.01, n))}
          ariaLabel="Track spacing in nautical miles"
          placeholder="0.4"
        />
        <NumberField
          label={area ? 'Search speed, SOG (kn)' : 'Search speed, STW (kn)'}
          value={patternSpeedKn}
          onCommit={(n) => setPatternSpeedKn(Math.max(0.1, n))}
          ariaLabel="Search speed in knots"
          placeholder="6"
        />
      </div>

      {area ? (
        <AreaPatternView area={area} />
      ) : (
        <WaterPatternView
          pattern={waterPattern!}
          patternId={patternId}
          patternStartedAt={patternStartedAt}
          startPattern={startPattern}
          stopPattern={stopPattern}
          progress={progress}
        />
      )}
    </Sheet>
  );
}

/** A ground-based area search: plotted on the chart, shown here as a summary. */
function AreaPatternView({ area }: { area: ReturnType<typeof areaSearchLegs> }) {
  return (
    <>
      <p className="acp-note">
        {area.id === 'parallel_track'
          ? 'Parallel track — long legs run parallel to the longest edge of the box.'
          : 'Creeping line — long legs run parallel to the shortest edge of the box.'}{' '}
        Ground-based, so it&rsquo;s plotted on the chart from the CSP (half a track space in from the corner). Steer the COG shown and use speed over the ground.
      </p>

      <div className="acp-kv" style={{ marginTop: 8 }}>
        <span className="k">Legs</span>
        <span className="v">{area.searchLegCount} × {area.longSpanNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k">Track spacing</span>
        <span className="v">{area.trackSpacingNm.toFixed(2)} nm</span>
      </div>
      <div className="acp-kv">
        <span className="k">Total distance</span>
        <span className="v">{formatDistance(area.totalDistanceNm)}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Total time at search speed</span>
        <span className="v">{formatLegTime(area.totalTimeSec)}</span>
      </div>

      <div className="acp-leglist" role="table" aria-label="Area search legs">
        <div className="acp-leg-head" role="row">
          <span role="columnheader">Leg</span>
          <span role="columnheader">COG</span>
          <span role="columnheader">Length</span>
          <span role="columnheader">Time</span>
        </div>
        {area.legs.map((leg) => (
          <div
            key={leg.legNumber}
            className={`acp-leg-row${leg.kind === 'cross' ? ' datum' : ''}`}
            role="row"
          >
            <span role="cell">{leg.legNumber}</span>
            <span role="cell">{headingLabel(leg.cogDeg, leg.cardinal)}</span>
            <span role="cell">{leg.lengthNm.toFixed(2)} nm</span>
            <span role="cell">
              {formatLegTime(leg.timeSec)}
              {leg.kind === 'cross' ? ' · cross' : ''}
            </span>
          </div>
        ))}
      </div>

      <p className="acp-note">
        These are courses over the ground, plotted as the track lines on the chart. The tide and leeway are already in the ground track, so make good the COG shown rather than steering it as a compass heading.
      </p>
    </>
  );
}

/** A through-water pattern: a heading-and-timer instruction list, not plotted. */
function WaterPatternView(props: {
  pattern: ReturnType<typeof buildPattern>;
  patternId: string;
  patternStartedAt: number | null;
  startPattern: () => void;
  stopPattern: () => void;
  progress: ReturnType<typeof patternProgress> | null;
}) {
  const { pattern, patternId, patternStartedAt, startPattern, stopPattern, progress } = props;
  return (
    <>
      {patternId === 'sector_search' && (
        <p className="acp-note">Sector search — RNLI ALB SAR Check Card 14.</p>
      )}

      <div className="acp-btn-row" style={{ marginTop: 8 }}>
        {patternStartedAt ? (
          <button type="button" className="acp-btn full" onClick={stopPattern}>
            Stop &amp; reset the timer
          </button>
        ) : (
          <button type="button" className="acp-btn full primary" onClick={startPattern}>
            Start the pattern
          </button>
        )}
      </div>

      {progress && !progress.done && (
        <div className="acp-mob-turn" role="status" aria-live="polite">
          <span className="acp-mob-turn-leg">
            Leg {progress.currentLegIndex + 1}/{pattern.legs.length} · steer{' '}
            {headingLabel(
              pattern.legs[progress.currentLegIndex].headingDeg,
              pattern.legs[progress.currentLegIndex].cardinal,
            )}
          </span>
          <span className="acp-mob-turn-count">Turn in {formatLegTime(progress.remainingInLegSec)}</span>
        </div>
      )}
      {progress && progress.done && (
        <p className="acp-note">Pattern complete. Restart it, widen the track spacing, or shift to the drifted datum and go again.</p>
      )}

      <div className="acp-leglist" role="table" aria-label="Search pattern legs">
        <div className="acp-leg-head" role="row">
          <span role="columnheader">Leg</span>
          <span role="columnheader">Heading</span>
          <span role="columnheader">STW</span>
          <span role="columnheader">Time</span>
        </div>
        {pattern.legs.map((leg, i) => {
          const current = progress && !progress.done && i === progress.currentLegIndex;
          return (
            <div
              key={leg.legNumber}
              className={`acp-leg-row${current ? ' current' : ''}${leg.atDatum ? ' datum' : ''}`}
              role="row"
            >
              <span role="cell">{leg.legNumber}</span>
              <span role="cell">{headingLabel(leg.headingDeg, leg.cardinal)}</span>
              <span role="cell">{leg.stwKn.toFixed(0)} kn</span>
              <span role="cell">
                {formatLegTime(leg.timeSec)}
                {current ? ` · ${formatLegTime(progress!.remainingInLegSec)} left` : ''}
                {leg.atDatum ? ' · datum' : ''}
              </span>
            </div>
          );
        })}
      </div>

      <p className="acp-note">
        Steer the compass HEADING shown, not the COG on the plotter, and hold the speed through the water, not over the ground. The tide and leeway will bend the ground track; the pattern is built in headings, so let the set take the boat and turn on the countdown.
        {patternId === 'sector_search'
          ? ' Every third leg returns to the datum: correct for it and restart the timer as you pass.'
          : ''}
      </p>
    </>
  );
}
